// RITA-1505 (Nutzungserfassung Phase 2c, Epic RITA-1496) — Formatierer der Auswertungsseite.
//
// Reine Funktionen ohne React, damit die Regeln einzeln prüfbar bleiben (usageFormat.test.ts).
//
// Kein Intl, kein toLocale*: unter kleinem ICU (Node ohne volle Gebietsdaten) fällt 'de-DE'
// still auf englische Trennzeichen zurück — aus '6.310' würde '6,310' und aus '14.09.2026'
// '9/14/2026'. Die Formate werden deshalb selbst gebaut, und Kalendertage der API
// ('YYYY-MM-DD', Europe/Berlin) werden zerlegt statt in ein Date gegeben: ein Date würde die
// Zeitzone des Browsers einrechnen und den Tag verschieben.
//
// Fehlende Werte werden zu '–'. Eine Null wäre eine Behauptung ('keine Nutzung'), wo die API
// 'nicht messbar' oder 'aus Datenschutzgründen nicht zeigbar' meint.
import { CLSThresholds, INPThresholds, LCPThresholds, TTFBThresholds } from 'web-vitals';
import type { UsageExtra, UsageVitalMetric } from '../types';
import { eimerStelle } from './eimerLeitern';

/** Anzeige für einen Wert, den die API nicht liefert (nicht messbar, unterdrückt). */
export const LEER = '–';

/** Zahl in deutscher Schreibweise: Tausenderpunkt, Dezimalkomma — ohne Intl. */
const deutscheZahl = (value: number, digits: number): string => {
  const feststellung = Math.abs(value).toFixed(digits);
  const [ganz, bruch] = feststellung.split('.');
  const mitPunkten = ganz.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const vorzeichen = value < 0 ? '-' : '';
  return bruch ? `${vorzeichen}${mitPunkten},${bruch}` : `${vorzeichen}${mitPunkten}`;
};

const istZahl = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Ganze Zahl mit Tausenderpunkt (Aufrufe, Sitzungen, Nutzer). */
export const formatCount = (value: number | null | undefined): string =>
  istZahl(value) ? deutscheZahl(value, 0) : LEER;

/** Dezimalzahl (Seiten je Sitzung, Scrolltiefe). */
export const formatNumber = (value: number | null | undefined, digits = 1): string =>
  istZahl(value) ? deutscheZahl(value, digits) : LEER;

/** Anteil (0..1) als Prozent. Die API liefert Anteile, keine Prozentzahlen. */
export const formatPercent = (value: number | null | undefined, digits = 1): string =>
  istZahl(value) ? `${deutscheZahl(value * 100, digits)} %` : LEER;

/** Dauer in ms, aufgelöst bis zur passenden Einheit (Sitzungsdauer ist bei 12 h gedeckelt). */
export const formatDuration = (ms: number | null | undefined): string => {
  if (!istZahl(ms)) return LEER;
  if (Math.abs(ms) < 1000) return `${deutscheZahl(ms, 0)} ms`;
  if (Math.abs(ms) < 60000) return `${deutscheZahl(ms / 1000, 1)} s`;
  if (Math.abs(ms) < 3600000) return `${deutscheZahl(ms / 60000, 1)} min`;
  return `${deutscheZahl(ms / 3600000, 1)} h`;
};

/**
 * Wert einer Ladeleistungs-Kennzahl (RITA-1515): LCP/INP/TTFB sind Millisekunden und werden wie
 * jede Dauer gezeigt; CLS ist ein dimensionsloser Bruchwert (0 = ruhig) mit zwei Stellen.
 */
export const formatVitalValue = (metric: UsageVitalMetric, value: number | null | undefined): string => {
  if (!istZahl(value)) return LEER;
  return metric === 'CLS' ? formatNumber(value, 2) : formatDuration(value);
};

/** Bewertung nach den Google-Schwellen — dieselben Worte wie in der Legende der Tabelle. */
export type VitalRating = 'gut' | 'verbesserungswürdig' | 'schlecht';

// Schwellen [gut bis, schlecht ab] aus dem Paket, das auch misst — keine zweite Wahrheit hier.
const VITAL_THRESHOLDS: Record<UsageVitalMetric, readonly [number, number]> = {
  LCP: LCPThresholds,
  INP: INPThresholds,
  CLS: CLSThresholds,
  TTFB: TTFBThresholds,
};

/** 'gut' bis einschließlich der ersten Schwelle, 'schlecht' oberhalb der zweiten; null ohne Wert. */
export const vitalRating = (metric: UsageVitalMetric, value: number | null | undefined): VitalRating | null => {
  if (!istZahl(value)) return null;
  const [gut, schlecht] = VITAL_THRESHOLDS[metric];
  if (value <= gut) return 'gut';
  if (value <= schlecht) return 'verbesserungswürdig';
  return 'schlecht';
};

const TAG = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Kalendertag 'YYYY-MM-DD' der API → '14.09.2026' (ohne Date, ohne Zeitzonenrechnung). */
export const formatDay = (day: string | null | undefined): string => {
  const treffer = typeof day === 'string' ? TAG.exec(day) : null;
  return treffer ? `${treffer[3]}.${treffer[2]}.${treffer[1]}` : LEER;
};

/** Tag ohne Jahr ('16.08.') — nur für den Fensteranfang innerhalb desselben Jahres. */
const tagOhneJahr = (day: string): string => {
  const treffer = TAG.exec(day);
  return treffer ? `${treffer[3]}.${treffer[2]}.` : LEER;
};

const jahr = (day: string): string => (TAG.exec(day) ?? [])[1] ?? '';

/**
 * Kurzes Fenster für die Heatmap-Leiste (RITA-1522): '24.08.–21.09.2026'. Dort ist kein Platz
 * für den vollen Zeitraumtext des Seitenkopfs, aber ein „Stand" allein nennt nur das ENDE des
 * Fensters — mit welchen Tagen die Karte gerechnet wurde, bliebe offen. Das Jahr am Anfang
 * entfällt, solange das Fenster in einem Jahr liegt (gleiche Regel wie in rangeText).
 */
export const fensterText = (dataFrom: string | null | undefined, dataUntil: string | null | undefined): string => {
  if (typeof dataFrom !== 'string' || typeof dataUntil !== 'string') return LEER;
  if (!TAG.test(dataFrom) || !TAG.test(dataUntil)) return LEER;
  const anfang = jahr(dataFrom) === jahr(dataUntil) ? tagOhneJahr(dataFrom) : formatDay(dataFrom);
  return `${anfang}–${formatDay(dataUntil)}`;
};

export interface UsageRangeText {
  days: number;
  dataFrom: string | null;
  dataUntil: string | null;
  missingDays: string[];
}

/**
 * Zeitraumtext des Seitenkopfs. Er nennt das TATSÄCHLICH abgedeckte Fenster, nicht die letzten
 * N Tage ab heute: der Zeitraum hängt am verdichteten Bestand und nicht an der Uhr. Tage ohne
 * Verdichtung werden ausgewiesen, damit niemand eine Lücke für einen Rückgang hält.
 */
export const rangeText = ({ days, dataFrom, dataUntil, missingDays }: UsageRangeText): string => {
  if (!dataFrom || !dataUntil || !TAG.test(dataFrom) || !TAG.test(dataUntil)) {
    return `Kein verdichteter Tag im Fenster (${days} Tage)`;
  }
  const anfang = jahr(dataFrom) === jahr(dataUntil) ? tagOhneJahr(dataFrom) : formatDay(dataFrom);
  const luecken = missingDays.length;
  const lueckenText = luecken === 0 ? '' : `, davon ${luecken} ${luecken === 1 ? 'Tag' : 'Tage'} ohne Verdichtung`;
  return `Fenster ${anfang}–${formatDay(dataUntil)} (${days} Tage)${lueckenText} · Stand: ${formatDay(dataUntil)}`;
};

/** Zähler aus einem JSON-Feld der API (extra, topCodes): nur endliche Zahlen zählen. */
const zaehler = (wert: unknown): Array<[string, number]> => {
  if (typeof wert !== 'object' || wert === null) return [];
  return Object.entries(wert as Record<string, unknown>).filter((eintrag): eintrag is [string, number] =>
    istZahl(eintrag[1]),
  );
};

/** Häufigster Schlüssel eines Zählerfeldes; bei Gleichstand der kleinere Schlüssel. */
const haeufigster = (wert: unknown): string => {
  const sortiert = zaehler(wert).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return sortiert.length > 0 ? sortiert[0][0] : '';
};

/**
 * Häufigster ANTWORTCODE einer Serveroperation — nicht der häufigste Fehlercode: topCodes zählt
 * jeden Code, auch den einer erfolgreichen Antwort (rollupUsageDaily.ts zählt außerhalb des
 * Fehlerzweigs). Die Fehlerzahl steht getrennt in `errors`.
 */
export const topResponseCode = (topCodes: UsageExtra): string => haeufigster(topCodes);

/** Zusatz an der Art eines Totklicks (RITA-1520): 'button-disabled'. */
const DISABLED_SUFFIX = 'disabled';
/** Kürzer als das wäre kein erkennbarer Anfang von 'disabled' mehr. */
const MIN_DISABLED_LENGTH = 4;

/**
 * Macht aus 'button-disabled' ein 'button (deaktiviert)'. Der Zusatz kann GEKÜRZT ankommen:
 * `element_kind` ist im Vertrag auf 16 Zeichen begrenzt, 'checkbox-disabled' wären 17. Deshalb
 * genügt ein Anfang von 'disabled'; ein Bindestrich mit anderem Wort bleibt unangetastet.
 */
const artText = (art: string): string => {
  const trenner = art.lastIndexOf('-');
  if (trenner <= 0) return art;
  const zusatz = art.slice(trenner + 1);
  if (zusatz.length < MIN_DISABLED_LENGTH || !DISABLED_SUFFIX.startsWith(zusatz)) return art;
  return `${art.slice(0, trenner)} (deaktiviert)`;
};

/**
 * Art des Bedienelements einer Klick-Zeile aus `extra.element_kind` ('button', 'link', 'tab');
 * bei Totklicks die Art des Elements, das bedienbar aussah ('button-disabled'). `labeled: 0`
 * heißt: in dieser Zeile trug kein Element ein data-usage — ohne diesen Zusatz sähe eine
 * unmarkierte Zeile aus wie eine markierte. Sammelzeilen und action-Ereignisse haben kein
 * `extra`; dort bleibt die Spalte leer.
 */
export const elementKindLabel = (extra: UsageExtra): string => {
  if (typeof extra !== 'object' || extra === null) return '';
  const art = artText(haeufigster((extra as Record<string, unknown>).element_kind));
  const beschriftet = (extra as Record<string, unknown>).labeled;
  const unmarkiert = beschriftet === 0;
  if (!art) return unmarkiert ? 'unmarkiert' : '';
  return unmarkiert ? `${art} (unmarkiert)` : art;
};

/**
 * Summe der Anteile einer Balkenliste. Der Nenner von `share` enthält auch Ausprägungen jenseits
 * der ausgegebenen 100 — die Summe liegt dann unter 1. Genau daran ist die Kappung ablesbar.
 */
export const shareSum = (rows: ReadonlyArray<{ share: number | null }>): number =>
  rows.reduce((summe, row) => summe + (istZahl(row.share) ? row.share : 0), 0);

/** Hinweis unter einer Balkenliste, wenn die Anteile die 100 % nicht erreichen. */
export const shareSumText = (rows: ReadonlyArray<{ share: number | null }>): string => {
  if (rows.length === 0) return '';
  const summe = shareSum(rows);
  return summe < 0.995 ? `Anteile summieren sich auf ${formatPercent(summe)} (Kappung).` : '';
};

/**
 * RITA-1523 (Versionsvergleich) — Richtung eines Perzentils auf seiner Eimer-Leiter.
 *
 * Die Änderungsspalte gibt auf Perzentilen KEINE Differenz aus (CONTRACT 3b V6): die Mediane sind
 * Näherungen aus den Tages-Histogrammen, und die Verweildauer-Leiter hat zwischen 10 s und 30 s
 * einen einzigen Eimer. Ein ausgewiesenes „+4,2 s" könnte allein ein Eimerwechsel bei
 * unveränderter Wirklichkeit sein. Deshalb: „unverändert", solange beide Werte im SELBEN Eimer
 * liegen; null, wenn eine Seite fehlt (eine unterdrückte oder nicht gemessene Zelle ist keine 0).
 */
export type Richtung = 'kürzer' | 'länger' | 'unverändert';

export const richtung = (
  vorher: number | null | undefined,
  nachher: number | null | undefined,
  leiter: readonly number[],
): Richtung | null => {
  if (!istZahl(vorher) || !istZahl(nachher)) return null;
  if (eimerStelle(vorher, leiter) === eimerStelle(nachher, leiter)) return 'unverändert';
  return nachher < vorher ? 'kürzer' : 'länger';
};

/**
 * Kante einer Stufenleiter als Text: so viele Nachkommastellen, wie die Kante braucht
 * ('0,5', '2', '100'). Gerundet wird vor dem Formatieren, weil eine Kante als Anteil mal 100 in
 * Gleitkomma knapp danebenliegen kann (0.1 * 100 = 10,000000000000002).
 */
const kanteText = (wert: number): string => {
  const gerundet = Math.round(wert * 100) / 100;
  if (Number.isInteger(gerundet)) return deutscheZahl(gerundet, 0);
  return deutscheZahl(gerundet, Number.isInteger(gerundet * 10) ? 1 : 2);
};

/**
 * RITA-1523 (Versionsvergleich) — SPANNE einer Quotenstufe (CONTRACT 3b V0a).
 *
 * Der Server gibt je Stand keine exakte Quote aus, sondern die untere KANTE ihrer Stufe: drei exakte
 * Quoten mit demselben Nenner legen diesen Nenner fest, und mit den Aufrufen der Seiten-Tabelle
 * wäre daraus die Aufrufzahl je Stand und die Restmenge „alle übrigen Stände" lesbar. Eine nackte
 * Zahl in der Zelle sähe deshalb wie eine Zusage aus, die die Antwort bewusst nicht macht — hier
 * steht die Spanne bis zur nächsten Kante.
 *
 * `leiter` ist die Leiter AUS DER ANTWORT (`rateLadder`, `pagesPerSessionLadder`), keine Kopie:
 * die Kanten gehören zur Zahl selbst. `faktor` rechnet Anteile in „je 100 Aufrufe" um (Vorgabe),
 * eine Leiter in eigener Einheit (Seiten je Sitzung) bekommt 1.
 *
 * Sonderfälle: die letzte Stufe ist oben offen („100 und mehr"); die unterste schließt alles
 * darunter ein, deshalb „0 bis unter 0,5" bei einer Leiter, die bei 0 anfängt, und „unter X" bei
 * einer, die höher anfängt.
 *
 * ZWEITE KOPIE dieser Beschriftung: `stufenSpanne` in
 * `apps/backend/src/services/usage/usageExportWorkbook.ts` schreibt dieselbe Spanne mit demselben
 * Wortlaut und denselben Regeln (Einrasten, „0 bis unter 0,5“, „und mehr“) in die Exportdatei; beide
 * Kopien haben ihren eigenen Test mit denselben Fällen, kein Test bindet sie aneinander. Die
 * Änderungskette steht in `docs/SYNC-CHAINS.md` („Beschriftung einer Quotenstufe“): wer hier den
 * Wortlaut ändert, ändert ihn dort mit.
 *
 * Trifft `step` keine Kante (Versionsversatz zwischen Antwortfeld und mitgelieferter Leiter), RASTET
 * die Beschriftung auf die nächstniedrigere Kante EIN, statt den Wert zu schreiben: die untere Kante
 * ist wahr, und `${step}` wäre die Zahl selbst. Auf zwei Nachkommastellen genügt sie für die
 * Rückrechnung — dort bleiben im Beispiel der Probe drei mögliche Nenner, also weiter eine scharfe
 * Schranke auf eine Einzelperson (readUsageDaily.ts, V0a). Ohne jede Kante gibt es keine Spanne:
 * dann steht ein Strich. Die RICHTUNG (`stufenRichtung`) rastet bewusst NICHT ein — sie darf
 * schweigen, weil Schweigen keine Zahl preisgibt.
 */
export const stufenText = (step: number | null | undefined, leiter: readonly number[], faktor = 100): string => {
  if (!istZahl(step)) return LEER;
  if (leiter.length === 0) return LEER;
  // Gleichheit auf der Kante zählt zu DIESER Stufe (wie `ladderStep` im Backend); liegt der Wert
  // zwischen zwei Kanten, gilt die untere.
  let stelle = -1;
  for (let i = 0; i < leiter.length; i += 1) {
    if (leiter[i] <= step) stelle = i;
  }
  if (stelle === -1) return `unter ${kanteText(leiter[0] * faktor)}`;
  const unten = kanteText(leiter[stelle] * faktor);
  if (stelle === leiter.length - 1) return `${unten} und mehr`;
  const oben = kanteText(leiter[stelle + 1] * faktor);
  if (stelle === 0 && leiter[0] > 0) return `unter ${oben}`;
  return `${unten} bis unter ${oben}`;
};

/** Bewertung einer Stufenänderung: bei Fehlern, Frustklicks und Rücksprüngen ist WENIGER besser. */
export type StufenBewertung = 'besser' | 'schlechter' | 'gleich';

export interface StufenAenderung {
  bewertung: StufenBewertung;
  /** Abstand in Stufen (immer positiv; 0 = dieselbe Stufe). */
  stufen: number;
  text: string;
}

/** Stufenzahl in Worten — „↓ zwei Stufen" liest sich schneller als „↓ 2 Stufen". */
const STUFEN_WORTE = ['', 'eine', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn'];

const stufenAnzahlText = (anzahl: number): string => {
  const wort = anzahl < STUFEN_WORTE.length ? STUFEN_WORTE[anzahl] : deutscheZahl(anzahl, 0);
  return `${wort} ${anzahl === 1 ? 'Stufe' : 'Stufen'}`;
};

/**
 * Richtung einer Quote über die STUFEN (RITA-1523, CONTRACT 3b V0a). Eine Differenz gibt es nicht
 * mehr: die Antwort trägt keine exakte Quote, und eine Differenz zweier Kanten wäre keine Aussage
 * über die Wirklichkeit, sondern über die Leiter. Gezählt wird deshalb der Abstand der Stufen.
 *
 * null, wenn eine Seite fehlt (unterdrückt oder nicht gemessen — eine fehlende Zelle ist keine 0)
 * oder eine der beiden Stufen nicht auf der mitgelieferten Leiter liegt: dann ist der Abstand nicht
 * zählbar, und eine erfundene Zahl von Stufen wäre schlimmer als keine Aussage.
 */
export const stufenRichtung = (
  vorher: number | null | undefined,
  nachher: number | null | undefined,
  leiter: readonly number[],
): StufenAenderung | null => {
  if (!istZahl(vorher) || !istZahl(nachher)) return null;
  const vorherStelle = leiter.indexOf(vorher);
  const nachherStelle = leiter.indexOf(nachher);
  if (vorherStelle === -1 || nachherStelle === -1) return null;
  const abstand = nachherStelle - vorherStelle;
  if (abstand === 0) return { bewertung: 'gleich', stufen: 0, text: 'gleiche Stufe' };
  const hinab = abstand < 0;
  const stufen = Math.abs(abstand);
  return {
    bewertung: hinab ? 'besser' : 'schlechter',
    stufen,
    text: `${hinab ? '↓' : '↑'} ${stufenAnzahlText(stufen)}`,
  };
};

/**
 * Tage, die ein Stand im Fenster liegen muss, damit die Änderung bewertet wird (CONTRACT 3b V6).
 * Unter drei Tagen ist der Median eines Standes von einzelnen Stunden geprägt — ein Deploy am
 * Nachmittag hätte einen halben Arbeitstag als Grundlage.
 */
export const MIN_VERGLEICH_TAGE = 3;

/** true = die Änderungsspalten bleiben gesperrt; ohne Tagesangabe (unterdrückte Zelle) ebenfalls. */
export const aenderungGesperrt = (daysA: number | null | undefined, daysB: number | null | undefined): boolean =>
  !istZahl(daysA) || !istZahl(daysB) || daysA < MIN_VERGLEICH_TAGE || daysB < MIN_VERGLEICH_TAGE;
